import React, { useMemo } from 'react';
import { Result, Spin } from 'antd';
import { showErrorNotification, showSuccessNotification } from '@/shared/utils/notify';
import { useNavigate, useParams } from 'react-router-dom';
import { useApprove, useGetActionFlowByScopeId } from '../../../api';
import type { ActionFlowRequestDTO } from '../../../api';
import ApprovalPage from './ApprovalPage';
import ApprovalDecision from './ApprovalDecision';

interface ActionFlowApprovalViewProps {
    /**
     * Additional custom components to register beyond the built-in ApprovalDecision.
     * Loan-specific or domain-specific components should be injected here by the
     * caller (e.g. LoanActionFlowApprovalView in the loans module).
     */
    extraCustomComponents?: Record<string, React.ComponentType<any>>;
    /** Called after a successful submission. Defaults to navigating back in history. */
    onSuccess?: () => void;
}

const ActionFlowApprovalView: React.FC<ActionFlowApprovalViewProps> = ({
    extraCustomComponents = {},
    onSuccess,
}) => {
    const { scopeId = '' } = useParams<{ scopeId: string }>();
    const navigate = useNavigate();

    const { data: actionFlowData, isLoading, error } = useGetActionFlowByScopeId(scopeId);
    const { mutateAsync } = useApprove();

    const handleSubmit = async (values: Record<string, unknown>) => {
        try {
            if (!scopeId) return;
            await mutateAsync({
                id: scopeId,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data: values as any,
            });
            showSuccessNotification('Request processed successfully');
            if (onSuccess) {
                onSuccess();
            } else {
                navigate(-1);
            }
        } catch {
            showErrorNotification('Failed to process request');
        }
    };

    // Built-in generic components merged with caller-injected domain components.
    // The caller's components take precedence so they can override defaults.
    const customComponents = useMemo(() => ({
        ApprovalDecision: (props: any) => <ApprovalDecision {...props} />,
        ...extraCustomComponents,
    }), [extraCustomComponents]);

    if (isLoading) {
        return <Spin size="large" />;
    }

    const actionFlow = actionFlowData as ActionFlowRequestDTO | undefined;

    if (error || !actionFlow || !actionFlow.domain || !actionFlow.subscriptionId) {
        return (
            <Result
                status="404"
                title="Request not found"
                subTitle="The requested action flow could not be loaded."
            />
        );
    }

    const { valueData, changesData, beforeData, ...rest } = actionFlow;
    const initialData = {
        ...rest,
        valueData,
        changesData,
        beforeData,
    };

    return (
        <ApprovalPage
            initialData={initialData}
            customComponents={customComponents}
            onSubmit={handleSubmit}
            domain={actionFlow.domain}
            subscriptionId={actionFlow.subscriptionId}
            nodeId={actionFlow.state}
        />
    );
};

export default ActionFlowApprovalView;
